
import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiUser, FiTrendingUp, FiAward, FiHeart, FiCheckCircle, FiArrowRight, FiMail, FiFileText, FiBriefcase} = FiIcons;

const Candidates = () => {
  const benefits = [
    {
      icon: FiUser,
      title: 'Personalized Approach',
      description: 'We take time to understand your career goals, not just your CV.'
    },
    {
      icon: FiTrendingUp,
      title: 'Career Growth',
      description: 'Access to exciting opportunities in cutting-edge robotics and AI companies.'
    },
    {
      icon: FiAward,
      title: 'Quality Opportunities',
      description: 'We work with innovative companies that value exceptional talent.'
    },
    {
      icon: FiHeart,
      title: 'Long-term Partnership',
      description: 'Ongoing career support and guidance throughout your journey.'
    }
  ];

  const roles = [
    'Robotics Engineers',
    'Computer Vision Engineers',
    'AI/ML Engineers',
    'Software Engineers',
    'Product Managers',
    'Engineering Leaders',
    'Research Scientists',
    'Technical Directors'
  ];

  const process = [
    {
      step: '1',
      title: 'Initial Consultation',
      description: 'We discuss your career goals, experience, and what you\'re looking for in your next role.'
    },
    {
      step: '2',
      title: 'Opportunity Matching',
      description: 'We match you with relevant opportunities that align with your skills and aspirations.'
    },
    {
      step: '3',
      title: 'Interview Preparation',
      description: 'We provide insights about the company and role, plus interview coaching if needed.'
    },
    {
      step: '4',
      title: 'Offer Support',
      description: 'We help with offer negotiation and ensure a smooth transition to your new role.'
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-secondary-600 to-secondary-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Your Next Career Move Starts Here
            </h1>
            <p className="text-xl text-secondary-100 max-w-3xl mx-auto mb-8">
              Join innovative robotics and AI companies that are building the future. We connect exceptional talent with exceptional opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://tidycal.com/trbe/career-coaching"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-secondary-600 px-8 py-3 rounded-lg font-medium hover:bg-secondary-50 transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiMail} className="h-5 w-5" />
                <span>Career Coaching Call</span>
              </a>
              <a
                href="mailto:candidate@trbe.ai"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-medium hover:bg-white hover:text-secondary-600 transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiFileText} className="h-5 w-5" />
                <span>Submit Your CV</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Why Work With Trbe?
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              We're not just another recruitment agency. We're career partners who understand the deep tech landscape.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-secondary-100 to-primary-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={benefit.icon} className="h-8 w-8 text-secondary-600" />
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-4">{benefit.title}</h3>
                <p className="text-neutral-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Roles We Recruit For
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              From individual contributors to leadership roles across the deep tech spectrum.
            </p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {roles.map((role, index) => (
              <motion.div
                key={role}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="bg-white rounded-lg p-6 text-center shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <div className="text-lg font-medium text-neutral-900">{role}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              How We Work Together
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Our streamlined process ensures you get the support you need at every step.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-secondary-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-4">{item.title}</h3>
                <p className="text-neutral-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Opportunities Link Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="bg-white rounded-2xl p-12 shadow-lg max-w-2xl mx-auto">
              <div className="w-20 h-20 bg-gradient-to-br from-secondary-100 to-primary-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiBriefcase} className="h-10 w-10 text-secondary-600" />
              </div>
              <h2 className="text-3xl font-bold text-neutral-900 mb-4">
                Explore Current Opportunities
              </h2>
              <p className="text-xl text-neutral-600 mb-8">
                Browse our latest job openings and find your next exciting role in deep tech.
              </p>
              <a
                href="https://jobs.trbe.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-secondary-600 text-white px-8 py-4 rounded-lg font-medium hover:bg-secondary-700 transition-colors duration-200"
              >
                <span>View All Jobs</span>
                <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Take the Next Step?
            </h2>
            <p className="text-xl text-secondary-100 mb-8 max-w-2xl mx-auto">
              Join our network of exceptional deep tech professionals and discover your next opportunity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:hello@trbe.ai"
                className="bg-white text-secondary-600 px-8 py-3 rounded-lg font-medium hover:bg-secondary-50 transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiMail} className="h-5 w-5" />
                <span>Get in Touch</span>
              </a>
              <a
                href="mailto:candidate@trbe.ai"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-medium hover:bg-white hover:text-secondary-600 transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiFileText} className="h-5 w-5" />
                <span>Upload CV</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Candidates;
